import { ChannelTermPage } from '@app/models/channel-term-page';
import { SearchResult } from '@app/models/search-result';
import { parseISO } from 'date-fns';
import { Observable } from 'rxjs';

export enum PageType {
  Content = 0,
  News = 1,
  NewsQuickRead = 2
}

export class Page {
  public Id: number;
  public Title: string;
  public SlugTitle: string;
  public Content: string;
  public BannerImageUrl: string;
  public BannerImageText: string;
  public BannerImageAltText: string;
  public PageType: PageType;
  public IsFeaturedPage: boolean;
  public IsCommentsEnabled: boolean;
  public ModifiedByUserUpnId: string;
  public OwnerUserUpnId: string;
  public LanguageTermId?: string;
  public Created: Date;
  public Modified: Date;
  public PublishingDate: Date;
  public ChannelTermPages: Array<ChannelTermPage>;
  public ModifiedByUserPhotoUrl$?: Observable<string>;
  public OwnerUserPhotoUrl$?: Observable<string>;
  public Path?: string;
  public IsSharePointPage?: boolean;
  public Notify: boolean;
  public SenderTags?: Array<any>;
  public ModifiedByUser$?: Observable<any>;

  constructor(opt?: Partial<Page>) {
    if (opt) {

      if (opt.Created && typeof opt.Created === 'string') {
        opt.Created = parseISO(opt.Created);
      }

      if (opt.Modified && typeof opt.Modified === 'string') {
        opt.Modified = parseISO(opt.Modified);
      }

      if (opt.PublishingDate && typeof opt.PublishingDate === 'string') {
        opt.PublishingDate = parseISO(opt.PublishingDate);
      }
    }

    Object.assign(this, opt);
  }

  static fromSearchResult(result: SearchResult, featuredPageMP: string, newsChannelMP: string, pageLanguageMP: string, pageType: PageType, newsSenderTagMP: string): Page {
    return new Page({
      Title: result ? result.Title : null,
      Content: result ? result.Description : null,
      Path: result ? result.Path : null,
      BannerImageUrl: result && result.PictureThumbnailURL && !result.PictureThumbnailURL.endsWith('/sitepagethumbnail.png') ? result.PictureThumbnailURL : null,
      PublishingDate: pageType === PageType.News ? (result ? result.FirstPublishedDate : null) : (result ? result.Created : null),
      Created: result ? result.Created : null,
      IsFeaturedPage: result[featuredPageMP] === '1' ? true : false,
      ChannelTermPages: result && result[newsChannelMP] ? this.parseManagedMetaDataStringAsChannelTerms(result[newsChannelMP]) : [],
      LanguageTermId: result && result[pageLanguageMP] ? this.getManagedMetaDataTermId(result[pageLanguageMP]) : '',
      PageType: pageType,
      IsSharePointPage: true,
      SenderTags: result && result[newsSenderTagMP] ? this.parseManagedMetaDataString(result[newsSenderTagMP]) : null
    });
  }

  static getManagedMetaDataTermId(metadatastring: string): string {
    // The language used always have 3 parts split by | and the guid starts with '#0' so slice away those two
    // Example:
    // "GP0|#8718ab35-a7d9-4d99-9945-1f5cc5742224;"
    // "L0|#08718ab35-a7d9-4d99-9945-1f5cc5742224|English;"
    // "GTSet|#d1a62611-0513-4d6a-92e2-9b250a08c5c3;"
    return metadatastring.split(';').filter(c => c.split('|').length === 3).map(c => c.split('|')[1])[0].slice(2);
  }

  static parseManagedMetaDataStringAsChannelTerms(channels: string): Array<ChannelTermPage> {
    // The channel used always have 3 parts split by | and the guid starts with '#0' so slice away those two
    // Example:
    // GP0|#1780e01b-8e93-4887-a7de-74e04ae2105d;L0|#01780e01b-8e93-4887-a7de-74e04ae2105d|Civil;GTSet|#d1a62611-0513-4d6a-92e2-9b250a08c5c3;GPP|#fdc4ef06-e425-40ca-8483-57e0e7118a0f
    // Is only the channel 'Civil' with guid '1780e01b-8e93-4887-a7de-74e04ae2105d'
    return channels.split(';').filter(c => c.split('|').length === 3).map(c => c.split('|')[1].slice(2)).map(id => ({ ChannelTermId: id, PageId: null }));
  }

  static parseManagedMetaDataString(metadatastring: string): string[] {
    // EXAMPLE:
    // "GP0|#fa47a309-9bf1-41c5-ae0e-c96fec918fb0;"
    // "L0|#0fa47a309-9bf1-41c5-ae0e-c96fec918fb0|Lund;"
    // "GTSet|#d1a62611-0513-4d6a-92e2-9b250a08c5c3;
    // "GPP|#bf474651-16af-430b-a1c7-3be0003f0f98;"
    // "GPP|#cd878a30-546c-4275-9e88-08114d03c079;"
    // "GPP|#48dfae2a-0b81-46dd-872f-d60b08a7c702;"
    // "GP0|#5ae92d29-5d4a-440e-b0a2-2e8bf0468ba0;"
    // "L0|#05ae92d29-5d4a-440e-b0a2-2e8bf0468ba0|Chef;"
    // "GPP|#601ef509-add9-4237-a80f-039f87615b6b;"
    // "GPP|#79a51917-cf59-4470-81c4-a74e12be48cc"
    return metadatastring.split(';').filter(c => c.split('|').length === 3).map(c => c.split('|')[2]);
  }

  toSearchResult(): SearchResult {
    const searchResult = new SearchResult();
    searchResult.Title = this.Title;
    searchResult.FirstPublishedDate = this.Created;
    searchResult.modifiedByUser$ = this.ModifiedByUser$;
    searchResult.LastModifiedTime = this.Modified;
    if (this.PageType === PageType.News) {
      searchResult.Path = `/pages/news/${this.SlugTitle}-${this.Id}`;
    } else if (this.PageType === PageType.Content) {
      searchResult.Path = `/pages/content/${this.SlugTitle}-${this.Id}`;
    }
    return searchResult;
  }
}
